import { formatDate, getLocaleFirstDayOfWeek, Location } from '@angular/common';
import { Component, ElementRef, HostListener, ViewChild, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbCalendar, NgbDate, NgbDateParserFormatter, NgbDatepickerNavigateEvent, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { EventsService } from 'src/app/services/events.service';

@Component({
  selector: 'app-events-resume',
  templateUrl: './events-resume.component.html',
  styleUrls: ['./events-resume.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class EventsResumeComponent {
  eventCards: any[] = [];
  eventId: any;

  currentPage: number = 1;
  lastPage: number = 1;
  loadingMore: boolean = false;
  private scrollTimeout: any = null;
  loadingSpinner: boolean = false;

  urlBack: string = '/home';

  // Filtros
  showFilters: boolean = false;
  searchText: string = '';
  private searchSubject = new Subject<string>();

  // Date picker
  stringDate: string = '';
  openDatePicker: boolean = false;
  fromDate!: any;
  toDate!: any;
  hoveredDate!: any;
  currentMonth: number;
  minDate: NgbDateStruct;
  model: NgbDateStruct;
  calendarStyle: any = {};
  @ViewChild('fechaDropdown') fechaDropdown!: ElementRef;

  constructor(
    private location: Location,
    private eventsService: EventsService,
    private router: Router,
    private route: ActivatedRoute,
    private calendar: NgbCalendar,
    public formatter: NgbDateParserFormatter
  ) {
    const navigation = this.router.getCurrentNavigation();
    const previousUrl = navigation?.previousNavigation?.finalUrl?.toString();
    if (previousUrl && !previousUrl.includes('event-sale')) {
      this.urlBack = previousUrl;
    }
    this.model = this.calendar.getToday();
    this.minDate = { day: 1, month: 0, year: 2020 };
    this.currentMonth = this.model.month;
  }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.eventId = params['idEvent'];
    });
    this.searchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(() => {
      this.applyFilters();
    });
    this.getEvents();
  }

  private buildFilters(): { search?: string; date_from?: string; date_to?: string } {
    const filters: { search?: string; date_from?: string; date_to?: string } = {};
    if (this.searchText?.trim()) {
      filters.search = this.searchText.trim();
    }
    if (this.fromDate) {
      const from = this.formatStringDate(this.formatter.format(this.fromDate), 'yyyy-MM-dd');
      filters.date_from = from!;
      filters.date_to = this.toDate
        ? this.formatStringDate(this.formatter.format(this.toDate), 'yyyy-MM-dd')!
        : from!;
    }
    return filters;
  }

  onSearchChange(value: string) {
    this.searchText = value;
    this.searchSubject.next(value);
  }

  clearFilters() {
    this.searchText = '';
    this.fromDate = null;
    this.toDate = null;
    this.stringDate = '';
    this.applyFilters();
  }

  private applyFilters() {
    this.currentPage = 1;
    this.eventCards = [];
    this.getEvents();
  }

  getEvents() {
    this.eventsService.getEventsActive(this.currentPage, this.buildFilters()).subscribe({
      next: (res) => {
        if (res?.success == true) {
          // Guardar información de paginación
          this.lastPage = res.data.last_page || 1;

          // Si es la primera página, reemplazar datos
          if (this.currentPage === 1) {
            this.eventCards = res.data.data;
          } else {
            // Si no es la primera página, añadir a los datos existentes
            this.eventCards = [...this.eventCards, ...res.data.data];
          }

          this.loadingMore = false;
          this.loadingSpinner = false;
        }
      }
    });
  }

  goBack() {
    this.router.navigate(['/home']);
  }

  // Date picker methods
  toggleDatePicker() {
    this.openDatePicker = !this.openDatePicker;
    if (this.openDatePicker && this.fechaDropdown) {
      const rect = this.fechaDropdown.nativeElement.getBoundingClientRect();
      this.calendarStyle = {
        top: (rect.bottom + 4) + 'px',
        left: (rect.right + 8) + 'px'
      };
    }
  }

  @HostListener('document:click', ['$event'])
  handleClickOutside(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (
      this.openDatePicker &&
      this.fechaDropdown &&
      !this.fechaDropdown.nativeElement.contains(target) &&
      !target.closest('.calendar-dropdown-fixed')
    ) {
      this.openDatePicker = false;
    }
  }

  changeDate(date: NgbDate) {
    if (!this.fromDate && !this.toDate) {
      this.fromDate = date;
    } else if (this.fromDate && !this.toDate && date.after(this.fromDate)) {
      this.toDate = date;
      this.openDatePicker = false;
    } else {
      this.toDate = null;
      this.fromDate = date;
    }
    const dateString = { min: this.formatter.format(this.fromDate), max: this.formatter.format(this.toDate) };
    let stringDates = this.formatStringDate(dateString.min)!;
    if (dateString.max && dateString.max !== '') {
      stringDates += ' - ' + this.formatStringDate(dateString.max);
    }
    this.stringDate = stringDates;
    this.applyFilters();
  }

  dateNavigate($event: NgbDatepickerNavigateEvent) {
    this.currentMonth = $event.next.month;
  }

  formatStringDate(date: any, format: string = 'dd MMM yy') {
    let dateFormat = null;
    if (date) {
      dateFormat = formatDate(new Date(date), format, 'es');
    }
    return dateFormat;
  }

  isHovered(date: NgbDate) {
    return this.fromDate && !this.toDate && this.hoveredDate && date.after(this.fromDate) && date.before(this.hoveredDate);
  }
  isMonday = (date: NgbDate) => this.calendar.getWeekday(date) == this.getFirstDayWeek();
  isSunday = (date: NgbDate) => this.calendar.getWeekday(date) == this.getLastDayWeek();
  isWeekend = (date: NgbDate) => this.calendar.getWeekday(date) >= 6;
  isDisabled = (date: NgbDate) => !(date.equals(this.minDate) || (date.after(this.minDate) && (date.month == this.currentMonth)));

  isFromDate(date: NgbDate) {
    return this.fromDate ? date.equals(this.fromDate) : false;
  }
  isToDate(date: NgbDate) {
    return this.toDate ? date.equals(this.toDate) : false;
  }
  isInside(date: NgbDate) {
    return this.toDate && date.after(this.fromDate) && date.before(this.toDate);
  }
  isRange(date: NgbDate) {
    return (date.equals(this.fromDate) && this.toDate) || (this.toDate && date.equals(this.toDate)) || this.isInside(date);
  }
  getFirstDayWeek() {
    return getLocaleFirstDayOfWeek('es');
  }
  getLastDayWeek() {
    let first = getLocaleFirstDayOfWeek('es');
    let lastCal = first + 6;
    if (lastCal > 7) { lastCal = first - (lastCal - 7); }
    return lastCal;
  }


  onScroll(event: any) {
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }

    if (this.currentPage < this.lastPage) {
      this.loadingSpinner = true;
    }

    this.scrollTimeout = setTimeout(() => {
      const el = event.target as HTMLElement;
      if (el) {
        const scrollPosition = el.scrollTop + el.clientHeight;
        const scrollHeight = el.scrollHeight;

        if (scrollPosition >= scrollHeight - 100 && !this.loadingMore && this.currentPage < this.lastPage) {
          this.loadingMore = true;
          this.currentPage++;
          this.getEvents();
        }
      }
    }, 500);
  }


}
